import React, { useEffect, useMemo, useState } from 'react';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import { get_api_url } from '../../../../utils/urls';
import { get_api, post_api } from '../../../../redux/api_funcs';
import PackageGroupedSelect from '../../../util_components/package_grouped_select';
import { groupPackageOptions } from '../../../../utils/planCategory';

interface ActivePackage {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  sessions_per_week?: number;
  plan_category?: string | null;
  is_high_volume?: number | null;
}

interface ScheduledPlanChange {
  type: string;
  package_uuid: string;
  package_name: string;
  package_price: number;
  currency: string;
  no_of_weeks: number;
  effective_date: string;
}

interface SchedulePlanChangeProps {
  onClose: () => void;
  student_uuid: string;
  student_plan_uuid: string;
  student_current_plan: string;
  is_prepaid: number;
  no_of_weeks_originally_enrolled_to: number;
  no_of_cycle_remaining: number;
  loadStudentData: () => void;
  set_notification_variable: (show: boolean, type: string, message: string) => void;
}

const WEEKS_OPTIONS = [4, 12, 24, 52];
// subscription_queue.reason is varchar(100) in prod, and the API's joi caps at 100.
const REASON_MAX_LENGTH = 100;
const CHANGE_TYPES = [
  { value: 'UPGRADE', label: 'Upgrade' },
  { value: 'DOWNGRADE', label: 'Downgrade' }
];

function SchedulePlanChange({
  onClose,
  student_uuid,
  student_plan_uuid,
  student_current_plan,
  is_prepaid,
  no_of_weeks_originally_enrolled_to,
  no_of_cycle_remaining,
  loadStudentData,
  set_notification_variable
}: SchedulePlanChangeProps) {
  const [packages, setPackages] = useState<ActivePackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [changeType, setChangeType] = useState('');
  const [targetPackageUUID, setTargetPackageUUID] = useState('');
  const [noOfWeeks, setNoOfWeeks] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const load_packages = async () => {
      try {
        const response = await get_api(get_api_url('active_packages'), true);
        setPackages(response?.data?.packages || []);
      } catch (error) {
        const msg =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Unable to load packages. Please try again.';
        setErrorMessage(msg);
        set_notification_variable(true, 'error', msg);
      } finally {
        setLoadingPackages(false);
      }
    };
    load_packages();
  }, []);

  const currentPackageName = useMemo(() => {
    const match = packages.find((p) => p.uuid === student_plan_uuid);
    return match?.name || student_current_plan;
  }, [packages, student_plan_uuid, student_current_plan]);

  // PROD-9767: grouped by plan type like Upgrade / Downgrade; the heading replaces the category suffix.
  const packageGroups = useMemo(
    () =>
      groupPackageOptions(
        packages.map((pkg) => ({
          value: pkg.uuid,
          label: pkg.name,
          sessionsPerWeek: pkg.sessions_per_week,
          planCategory: pkg.plan_category
        })),
        student_plan_uuid
      ),
    [packages, student_plan_uuid]
  );

  const canSubmit = !!changeType && !!targetPackageUUID && !!noOfWeeks && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage('');

    const payload = {
      student_uuid,
      target_package_uuid: targetPackageUUID,
      type: changeType,
      no_of_weeks: Number(noOfWeeks),
      source: 'HRIDAYA',
      reason: reason.trim()
    };

    try {
      const response = await post_api(get_api_url('schedule_plan_change'), payload, true);
      if (response?.data?.status === 'success') {
        const change: ScheduledPlanChange = response.data.scheduled_plan_change;
        set_notification_variable(
          true,
          'success',
          `${change.type === 'UPGRADE' ? 'Upgrade' : 'Downgrade'} to ${change.package_name} (${
            change.no_of_weeks
          } weeks, ${change.currency} ${change.package_price}) scheduled for ${
            change.effective_date
          }`
        );
        loadStudentData();
        onClose();
      }
    } catch (error) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as Error)?.message ||
        'Unable to schedule the plan change. Please try again.';
      setErrorMessage(msg);
      set_notification_variable(true, 'error', msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full min-w-0 px-1">
      <hr className="mb-5 border-t border-gray-200" />

      <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Current plan</p>
        <p className="mt-1 text-base font-medium text-gray-800">
          {currentPackageName}
          {is_prepaid === 0 ? ' / Monthly' : ''}
        </p>
        {is_prepaid === 1 && (
          <p className="mt-1.5 text-sm text-gray-600">
            {no_of_weeks_originally_enrolled_to} weeks enrolled · {no_of_cycle_remaining} cycles
            left
          </p>
        )}
      </div>

      <fieldset className="mb-5">
        <legend className="mb-2 text-sm font-semibold text-gray-700">Change type</legend>
        <div className="flex gap-6">
          {CHANGE_TYPES.map((option) => (
            <label key={option.value} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="plan_change_type"
                value={option.value}
                checked={changeType === option.value}
                onChange={(e) => {
                  setChangeType(e.target.value);
                  setErrorMessage('');
                }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <PackageGroupedSelect
        label="Target package"
        groups={packageGroups}
        onChange={(e) => {
          setTargetPackageUUID(e.target?.value || '');
          setErrorMessage('');
        }}
        name="target_package_uuid"
        value={targetPackageUUID}
        defaultOption={loadingPackages ? 'Loading packages...' : 'Select a package'}
        disabled={loadingPackages}
        style={{ width: '100%' }}
      />

      <CustomSingleSelect
        label="Number of weeks"
        options={WEEKS_OPTIONS.map((w) => ({ label: w.toString(), value: w.toString() }))}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setNoOfWeeks(e.target?.value || '');
          setErrorMessage('');
        }}
        name="no_of_weeks"
        value={noOfWeeks}
        style={{ width: '100%' }}
      />

      <div className="mb-3">
        <label className="block mt-6 mb-1 text-sm font-semibold text-gray-700">
          Reason
          <span className="ml-2 font-normal text-gray-500">
            {reason.length}/{REASON_MAX_LENGTH}
          </span>
        </label>
        <textarea
          className="h-[80px] w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={reason}
          maxLength={REASON_MAX_LENGTH}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is the plan being changed?"
        />
      </div>

      {errorMessage && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      <div className="mt-5 flex justify-center gap-4">
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded border border-gray-400 bg-gray-200 px-6 py-2 text-black hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full rounded border px-6 py-2 text-white ${
            canSubmit
              ? 'cursor-pointer border-emerald-700 bg-emerald-600 hover:bg-emerald-700'
              : 'cursor-not-allowed border-emerald-700 bg-emerald-600 opacity-60'
          }`}
        >
          {submitting ? 'Scheduling...' : 'Schedule plan change'}
        </button>
      </div>
    </div>
  );
}

export default SchedulePlanChange;
